import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { BRAND, BRAND_STATIC } from "../data/content";
import { getCollectionVersions } from "../services/versionService";
import { getCache, setCache, getVersions, setVersion } from "../lib/cache";

const DEFAULT_CONTACT = {
  phone: BRAND.phone,
  whatsapp: BRAND.whatsapp,
  email: BRAND.email,
  address: BRAND.address,
  instagram: BRAND.instagram,
  tiktok: BRAND.tiktok,
  facebook: BRAND.facebook,
  socials: {
    instagram: BRAND.socials.instagram,
    tiktok: BRAND.socials.tiktok,
    facebook: BRAND.socials.facebook,
  },
};

export function useBrandData() {
  const [contact, setContact] = useState(DEFAULT_CONTACT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        // Check cache first
        const [serverVersions, cachedVersion, cachedData] = await Promise.all([
          getCollectionVersions(),
          getVersions().then((v) => v.brand),
          getCache("brand"),
        ]);

        const serverVersion = serverVersions.brand;

        if (
          cachedData &&
          cachedVersion &&
          serverVersion &&
          serverVersion === cachedVersion
        ) {
          if (mounted) {
            setContact(cachedData);
            setLoading(false);
          }
          return;
        }

        // Fetch fresh
        const snap = await getDoc(doc(db, "brand", "site"));
        if (mounted) {
          let data = { ...DEFAULT_CONTACT };
          if (snap.exists()) {
            const d = snap.data();
            data = {
              phone: d.phone || DEFAULT_CONTACT.phone,
              whatsapp: d.whatsapp || DEFAULT_CONTACT.whatsapp,
              email: d.email || DEFAULT_CONTACT.email,
              address: d.address || DEFAULT_CONTACT.address,
              instagram: d.instagram || DEFAULT_CONTACT.instagram,
              tiktok: d.tiktok || DEFAULT_CONTACT.tiktok,
              facebook: d.facebook || DEFAULT_CONTACT.facebook,
              socials: {
                instagram:
                  d.socials?.instagram || DEFAULT_CONTACT.socials.instagram,
                tiktok: d.socials?.tiktok || DEFAULT_CONTACT.socials.tiktok,
                facebook:
                  d.socials?.facebook || DEFAULT_CONTACT.socials.facebook,
              },
            };
          }
          setContact(data);
          setLoading(false);

          // Update cache
          await Promise.all([
            setCache("brand", data),
            setVersion(
              "brand",
              serverVersion || new Date().toISOString()
            ),
          ]);
        }
      } catch (err) {
        console.warn("Brand data unavailable, using fallback:", err.message);
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const brand = { ...BRAND_STATIC, ...contact };
  return { brand, loading };
}
